import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, startOfWeek, addDays, getWeek, addWeeks, subWeeks, addMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Pencil, Trash2, ChevronLeft, ChevronRight, Search, Download, Calendar as CalendarIcon, Baby, BookOpen as BookOpenIcon, School, Building, GraduationCap, ChevronDown, X,
  Clock, XCircle, RotateCcw, Link2
} from 'lucide-react';
import RecurringModal from './RecurringModal';
import { api } from '../services/api';
import PorteurNotification from "./ui/PorteurNotification";
import ProtectedComponent from './ProtectedComponent';
import LoadingSpinner from './LoadingSpinner';
import EmploiDuTempsAnnuelModal from './EmploiDuTempsAnnuelModal';
import { usePermissions } from '../hooks/usePermissions';
import ConfirmDialog from './ConfirmDialog';
import ConsultationVolumeHoraire from './ConsultationVolumeHoraire';
import DeleteBulkTimetableModal from './DeleteBulkTimetableModal';
import ExportTimetableModal from './ExportTimetableModal';

import EditTimeSlotModal from './EditTimeSlotModal';
import CancelTimeSlotModal from './CancelTimeSlotModal';



const Calendar = ({ events, filters, onEdit, onDelete, onCancel }) => {
  // Initialiser avec la date courante au lieu d'une date arbitraire
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(getWeek(new Date()));
  const [showDatePicker, setShowDatePicker] = useState(false);


  // Fonction pour aller à la semaine courante
  const goToCurrentWeek = () => {
    const today = new Date();
    setCurrentDate(today);
    setCurrentWeek(getWeek(today));
  };

  const nextWeek = () => {
    const newDate = addWeeks(currentDate, 1);
    setCurrentDate(newDate);
    setCurrentWeek(getWeek(newDate));
  };

  const prevWeek = () => {
    const newDate = subWeeks(currentDate, 1);
    setCurrentDate(newDate);
    setCurrentWeek(getWeek(newDate));
  };

  const goToDate = (selectedDate) => {
  setCurrentDate(selectedDate);
  setCurrentWeek(getWeek(selectedDate));
  setShowDatePicker(false);
};


// Ajouter cet useEffect pour fermer le DatePicker en cliquant dehors
useEffect(() => {
  const handleClickOutside = (event) => {
    if (showDatePicker && !event.target.closest('.relative')) {
      setShowDatePicker(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, [showDatePicker]);

  
  // Si aucun événement et aucun filtre sélectionné, afficher le message
 const renderTimeSlots = () => {
  if (!filters.class && !filters.teacher) {
    return (
      <div className="flex items-center justify-center h-96 text-center p-8">
        <div>
          <CalendarIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Veuillez sélectionner une classe
          </h3>
          <p className="text-gray-600">
            Choisissez une classe que vous souhaitez afin de consulter son emploi du temps
          </p>
        </div>
      </div>
    );
  }

  if (filters.teacher && events.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-center p-8">
        <div>
          <CalendarIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun créneau pour cet enseignant
          </h3>
          <p className="text-gray-600">
            Cet enseignant n'a pas de créneaux programmés pour cette semaine
          </p>
        </div>
      </div>
    );
  }

  if (filters.class && events.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-center p-8">
        <div>
          <CalendarIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun cours programmé
          </h3>
          <p className="text-gray-600">
            Cette classe n'a pas de cours programmés pour cette semaine
          </p>
        </div>
      </div>
    );
  }

  let weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  let days = Array.from({ length: 6 }, (_, i) => addDays(weekStart, i));

  // La grille etait figee sur 7h-19h, alors qu'aucun champ d'heure du module
  // n'avait de borne : un creneau pose a 6h ou a 20h etait accepte, ecrit pour
  // toute l'annee, compte dans les volumes horaires — et n'apparaissait NULLE
  // PART. Donc impossible a corriger d'un clic, puisqu'on ne pouvait pas
  // l'atteindre. La production en porte (constate le 13/10/2026).
  //
  // Les champs sont bornes par ailleurs : cette ouverture ne sert qu'a
  // rattraper l'existant. Une seule passe sur les creneaux, la ou la grille
  // en fait deja une par heure et par jour.
  let premiereHeure = 7;
  let derniereHeure = 19;
  events.forEach((event) => {
    const heure = new Date(event.date).getHours();
    if (!Number.isInteger(heure)) return;
    if (heure < premiereHeure) premiereHeure = heure;
    if (heure > derniereHeure) derniereHeure = heure;
  });
  const hoursArray = Array.from(
    { length: derniereHeure - premiereHeure + 1 },
    (_, i) => i + premiereHeure
  );

  return (
    <div className="grid grid-cols-6">
      {days.map((day, dayIndex) => (
        <div key={day.toISOString()} className="border-r min-h-screen">
          <div className="h-16 border-b p-2 text-center bg-gray-50">
            <div className="font-bold">{format(day, 'EEEE', { locale: fr })}</div>
            <div className="text-sm">{format(day, 'd MMMM', { locale: fr })}</div>
          </div>
          
          <div className="relative">
            {hoursArray.map(hour => {
              const currentDayEvents = events.filter(event => {
                const eventDate = new Date(event.date);
                return format(eventDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd') &&
                       eventDate.getHours() === hour;
              });

      
            return currentDayEvents.map((event, eventIndex) => (


            <div
            key={`${event.id}-${eventIndex}`}
            className={`mx-2 my-1 p-2 rounded-lg shadow relative ${
              event.est_annule 
                ? 'bg-red-50 border-2 border-red-300' 
                : 'bg-green-50 border-2 border-green-300'
            }`}
          >
              <div className="flex justify-between">
                <div className="flex-1">
                  <div className={`font-semibold text-sm ${event.est_annule ? 'line-through text-gray-500' : ''}`}>
                    {event.title}
                  </div>
                  
                 {!!event.est_annule && (
                    <div className="text-xs text-red-600 font-medium">
                      <XCircle size={12} className="inline shrink-0" />
                      {' '}Créneau annulé
                    </div>
                  )}
                  
                 
                 {!!event.est_cours_mutualise && (
                  <div className="mt-1">
                    {event.classes_mutualisees && event.classes_mutualisees.length > 0 ? (
                      <div className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                        <Link2 size={12} className="inline shrink-0" />
                        {' '}Cours commun avec : {event.classes_mutualisees.join(', ')}
                      </div>
                    ) : (
                      <div className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium italic">
                        Cours mutualisé
                      </div>
                    )}
                  </div>
                )}
                
                            
                                  
                  <div className="text-xs text-gray-600">{event.teacher}</div>
                  <div className="text-xs text-gray-600 font-medium">{event.className}</div>
                  <div className="text-xs">
                    {format(new Date(event.date), 'HH:mm')} - 
                    {format(addMinutes(new Date(event.date), event.duration * 60), 'HH:mm')}
                  </div>
                </div>
                
     

               {/* 🔥 REMPLACEZ TOUTE LA SECTION !event.est_annule PAR CECI */}
              <div className="flex flex-col gap-1">
                {/* Bouton Edit */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(event);
                  }}
                  className="rounded-full w-6 h-6 flex items-center justify-center transition-opacity hover:opacity-80"
                  style={{ background: 'linear-gradient(to right, #319190, #63b5b4)' }}
                >
                  <Pencil className="h-3 w-3 text-white" />
                </button>
                
                {/* Bouton Annuler/Réactiver dynamique */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(event);
                  }}
                  className="rounded-full w-6 h-6 flex items-center justify-center transition-opacity hover:opacity-90"
                  style={{ 
                    background: event.est_annule 
                      ? 'linear-gradient(to right, #10b981, #059669)'  // Vert pour réactiver
                      : 'linear-gradient(to right, #f59e0b, #d97706)'  // Orange pour annuler
                  }}
                  title={event.est_annule ? "Réactiver le cours" : "Annuler le cours"}
                >
                  {event.est_annule ? (
                    <RotateCcw className="h-3 w-3 text-white" />
                  ) : (
                    <XCircle className="h-3 w-3 text-white" />
                  )}
                </button>

                {/* Bouton Delete - seulement si non annulé */}
                {!event.est_annule && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(event);
                    }}
                    className="rounded-full w-6 h-6 flex items-center justify-center transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(to right, #c66d59, #ae7467)' }}
                  >
                    <Trash2 className="h-3 w-3 text-white" />
                  </button>
                )}
              </div>
              </div>
            </div>
              ));


            })}


            
          </div>
        </div>
      ))}
    </div>
  );
};




  // Vérifier si on est sur la semaine courante
  const isCurrentWeek = getWeek(new Date()) === currentWeek;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex justify-between items-center p-4 border-b">
        <div className="flex gap-2 items-center">
          <Button variant="outline" onClick={prevWeek}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={nextWeek}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          
          {/* Bouton pour revenir à la semaine courante */}
          {!isCurrentWeek && (
            <Button 
              variant="outline" 
              onClick={goToCurrentWeek}
              className="ml-2 text-blue-600 border-blue-600 hover:bg-blue-50"
            >
              Semaine courante
            </Button>
          )}
        </div>
        
        <div className="font-semibold flex items-center gap-2">
  <div className="relative">
    <button
      onClick={() => setShowDatePicker(!showDatePicker)}
      className="flex items-center gap-2 p-1 rounded hover:bg-gray-100 transition-colors"
      title="Sélectionner une date"
    >
      <CalendarIcon className="h-4 w-4 text-gray-600 hover:text-[#319190]" />
      <span>Semaine {currentWeek} - {format(currentDate, 'yyyy')}</span>
    </button>
    
    {/* DatePicker dropdown */}
    {showDatePicker && (
      <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Aller à la date :
          </label>
          <input
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#319190] focus:border-[#319190]"
            value={format(currentDate, 'yyyy-MM-dd')}
            onChange={(e) => {
              if (e.target.value) {
                goToDate(new Date(e.target.value));
              }
            }}
          />
        </div>
        
        {/* Raccourcis rapides */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-600 mb-2">Raccourcis :</p>
          <button
            onClick={() => goToDate(new Date())}
            className="block w-full text-left px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
          >
            Aujourd'hui
          </button>
          <button
            onClick={() => goToDate(addWeeks(new Date(), 1))}
            className="block w-full text-left px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
          >
            Semaine prochaine
          </button>
          <button
            onClick={() => goToDate(subWeeks(new Date(), 1))}
            className="block w-full text-left px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
          >
            Semaine dernière
          </button>
        </div>
        
        <button
          onClick={() => setShowDatePicker(false)}
          className="mt-3 w-full px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
        >
          Fermer
        </button>
      </div>
    )}
  </div>
  
  {isCurrentWeek && (
    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
      semaine actuelle
    </span>
  )}
</div>



      </div>
      <div className="overflow-auto">{renderTimeSlots()}</div>
    </div>
  );
};




const TeacherSearchSelect = ({ teachers, selectedTeacher, onSelect, onClear }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredTeachers = teachers.filter(teacher => 
    `${teacher.nom} ${teacher.prenom}`.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.teacher-search-container')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="teacher-search-container relative w-full">
      <label className="block text-[13px] font-medium text-gray-700 mb-1.5">
        Ou voir les créneaux d'un enseignant
      </label>
      
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un enseignant..."
          value={selectedTeacher ? `${selectedTeacher.nom} ${selectedTeacher.prenom}` : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (selectedTeacher) {
              onClear();
            }
          }}
          onFocus={() => setIsOpen(true)}
          className="w-full pl-10 pr-4 py-2.5 text-[13px] border border-gray-300 rounded-lg focus:border-[#319190] focus:ring-1 focus:ring-[#319190] outline-none"
        />
        
        {selectedTeacher && (
          <button
            onClick={() => {
              onClear();
              setSearchTerm('');
            }}
            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {isOpen && !selectedTeacher && filteredTeachers.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredTeachers.map(teacher => (
            <div
              key={teacher.id}
              onClick={() => {
                onSelect(teacher);
                setSearchTerm('');
                setIsOpen(false);
              }}
              className="p-2.5 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
            >
              <div className="font-medium text-gray-900">
                {teacher.nom} {teacher.prenom}
              </div>
              <div className="text-sm text-gray-500">
                {teacher.matiere || 'Enseignant'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



const TimetableManager = ({ activeYear, onStatsUpdate }) => {
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showEmploiDuTempsModal, setShowEmploiDuTempsModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showVolumeHoraire, setShowVolumeHoraire] = useState(false);  
  // Le premier chargement : cinq appels en parallele, puis les creneaux de
  // l'annee. La grille restait vide sans un mot - l'etat etait la, en
  // commentaire, et le `finally` du chargement vide.
  const [premierChargement, setPremierChargement] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState({ class: '', teacher: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [toastInfo, setToastInfo] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const { hasGranularPermission } = usePermissions();
  const [sections, setSections] = useState([]);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [eventToCancel, setEventToCancel] = useState(null);
  const [showDeleteBulkModal, setShowDeleteBulkModal] = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelMode, setCancelMode] = useState('cancel'); // 'cancel' ou 'reactivate'

  // États pour les filtres, tenus par la page elle-même
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');








  // ========== FONCTIONS HELPERS POUR LES FILTRES ==========
  
  // Fonction pour obtenir l'icône de section
  const getSectionIcon = useCallback((sectionName) => {
    const name = sectionName.toLowerCase();
    if (name.includes('préscolaire') || name.includes('prescolaire') || name.includes('maternelle')) {
      return <Baby className="w-4 h-4" />;
    }
    if (name.includes('primaire') || name.includes('élémentaire')) {
      return <BookOpenIcon className="w-4 h-4" />;
    }
    if (name.includes('collège') || name.includes('college')) {
      return <School className="w-4 h-4" />;
    }
    if (name.includes('lycée') || name.includes('lycee')) {
      return <Building className="w-4 h-4" />;
    }
    return <GraduationCap className="w-4 h-4" />;
  }, []);

  // Toggle expansion des sections
  const toggleExpansion = useCallback((sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }, []);

  // Handler changement classe
  const handleClassChange = useCallback((classId) => {
    setSelectedClass(classId);
    setFilters(prev => ({ ...prev, class: classId }));
    if (classId && selectedTeacher) {
      setSelectedTeacher(null);
      setFilters(prev => ({ ...prev, teacher: '' }));
    }
  }, [selectedTeacher]);

  // Handler sélection enseignant
  const handleTeacherSelect = useCallback((teacher) => {
    setSelectedTeacher(teacher);
    setFilters(prev => ({ ...prev, teacher: teacher.id }));
    if (selectedClass) {
      setSelectedClass('');
      setSelectedSection('');
      setFilters(prev => ({ ...prev, class: '', section: '' }));
    }
  }, [selectedClass]);

  // Handler clear enseignant
  const handleTeacherClear = useCallback(() => {
    setSelectedTeacher(null);
    setFilters(prev => ({ ...prev, teacher: '' }));
  }, []);

  // Handler reset
  const handleReset = useCallback(() => {
    setSelectedSection('');
    setSelectedClass('');
    setSelectedTeacher(null);
    setFilters({ class: '', teacher: '' });
  }, []);

  // Handlers pour boutons d'action
  const onVolumeHoraireClick = useCallback(() => {
    setShowVolumeHoraire(prev => !prev);
  }, []);

  const onExportClick = useCallback(() => {
    setShowExportModal(true);
  }, []);

  // Grouper classes par section (avec protection)
  const classesBySection = useMemo(() => {
    if (!classes || classes.length === 0) return {};
    
    return classes.reduce((acc, classe) => {
      const sectionId = classe.section_id;
      if (!acc[sectionId]) {
        acc[sectionId] = [];
      }
      acc[sectionId].push(classe);
      return acc;
    }, {});
  }, [classes]);

  // Trier les sections (avec protection)
  const sortedSections = useMemo(() => {
    if (!sections || sections.length === 0) return [];
    
    return [...sections].sort((a, b) => {
      if (a.ordre && b.ordre) return a.ordre - b.ordre;
      return a.nom.localeCompare(b.nom);
    });
  }, [sections]);

  // ========== FIN FONCTIONS HELPERS ==========




// Fonction de rechargement après création emploi du temps
// La generation annuelle annonce elle-meme son succes, puis rend la main :
// ici, on ferme et on recharge la grille.
const handleEmploiDuTempsCreated = async () => {
  setShowEmploiDuTempsModal(false);
  await loadEvents();
  updateParentStats();
};


const handleCancelClick = (event) => {
  setSelectedEvent(event);
  setCancelMode(event.est_annule ? 'reactivate' : 'cancel');
  setShowCancelModal(true);
};




const handleCancelSubmit = async (formData) => {
  try {
    if (!selectedEvent?.id) {
      throw new Error("Identifiant du créneau manquant");
    }

    setToastInfo({
      message: formData.mode === 'reactivate' ? 'Réactivation en cours...' : 'Annulation en cours...',
      type: 'loading'
    });

    const response = await api.timetable.toggleCancel(selectedEvent.id, formData);
    
    if (response?.success) {
      // Le serveur dit combien de creneaux il a REELLEMENT touches, et il dit
      // vrai. L'ecran ecrivait `|| 1` : quand la portee ne correspondait a
      // rien, il annoncait « 1 créneau annulé avec succès » sur un travail qui
      // n'avait rien fait. Le cas s'atteint sans effort : une periode choisie
      // hors de l'annee, ou un creneau deja dans l'etat demande.
      const count = Number(response.affectedRows ?? 0);
      const action = formData.mode === 'reactivate' ? 'réactivé' : 'annulé';

      await loadEvents();
      updateParentStats();

      if (count === 0) {
        // On NE FERME PAS la fenetre : c'est la seule facon de laisser
        // corriger la portee ou les dates sans tout ressaisir.
        setToastInfo({
          message: `Aucun créneau ne correspondait à cette portée : rien n'a été ${action}.`,
          type: "info",
          title: "Aucun créneau concerné",
          long: true
        });
        return;
      }

      setShowCancelModal(false);
      setSelectedEvent(null);
      setToastInfo({
        message: `${count} créneau${count > 1 ? 'x' : ''} ${action}${count > 1 ? 's' : ''} avec succès`,
        type: "success"
      });
    } else {
      // Sans ce `else`, un refus laissait le toast « en cours » a l'ecran
      // pour toujours : un chargement perpetuel, et pas un mot sur la cause.
      setToastInfo({
        message: response?.message || "L'opération a été refusée par le serveur.",
        type: "error",
        title: "Opération impossible",
        long: true
      });
    }
  } catch (error) {
    console.error('❌ Erreur:', error);
    setToastInfo({
      message: error.message || "Erreur lors de l'opération",
      type: "error"
    });
  }
};




  // AJOUTEZ CETTE LIGNE ICI :
 const filteredEvents = useMemo(() => {
  return events.filter(event => {
    if (filters.class && event.classId !== parseInt(filters.class)) return false;
    if (filters.teacher && event.teacherId !== parseInt(filters.teacher)) return false;
    return true;
  });
}, [events, filters.class, filters.teacher]);








  // Fonction pour mettre à jour les stats du parent
  const updateParentStats = useCallback(() => {
  if (onStatsUpdate) {
    onStatsUpdate({
      totalCreneaux: filteredEvents.length,
      totalCreneauxSemaine: filteredEvents.filter(event => {
        const eventWeek = getWeek(new Date(event.date));
        const currentWeek = getWeek(new Date());
        return eventWeek === currentWeek;
      }).length
    });
  }
}, [onStatsUpdate]);

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setShowEditModal(true);
  };


const handleEditSubmit = async (formData) => {
  try {
    if (!selectedEvent?.id) {
      throw new Error("Identifiant du créneau manquant");
    }


    // 🆕 Toast de chargement
    setToastInfo({
      message: 'Modification en cours...',
      type: 'loading'
    });

    const dataToSend = {
      classe_id: selectedEvent.classId,
      enseignant_id: formData.enseignant_id,
      matiere_id: formData.matiere_id,
      affectation_id: selectedEvent.affectation_id, 
      jour_semaine: formData.jour_semaine,
      heure_debut: formData.heure_debut,
      heure_fin: formData.heure_fin,
      salle: selectedEvent.salle || '',
      annee_scolaire_id: activeYear.id,
      scope: formData.scope,
      ...(formData.scope === 'period' && {
        date_debut: formData.date_debut,
        date_fin: formData.date_fin
      })
    };


    const response = await api.timetable.update(selectedEvent.id, dataToSend);
    

    if (response?.success) {
      // Meme regle que pour l'annulation : on annonce ce que le serveur a
      // fait, pas ce qu'on esperait qu'il fasse.
      const count = Number(response.affectedRows ?? 0);

      await loadEvents();
      updateParentStats();

      if (count === 0) {
        setToastInfo({
          message: "Aucun créneau ne correspondait à cette portée : rien n'a été modifié.",
          type: "info",
          title: "Aucun créneau concerné",
          long: true
        });
        return;
      }

      setShowEditModal(false);
      setSelectedEvent(null);
      setToastInfo({
        message: `${count} créneau${count > 1 ? 'x' : ''} modifié${count > 1 ? 's' : ''} avec succès`,
        type: "success"
      });
    } else {
      // ⚠️ PAS de `throw` ici. handleResponse RETOURNE les erreurs metier
      // (400, 403, 409) au lieu de lever : c'est voulu et documente, toute
      // l'application en depend. Un `throw` fabrique une Error nue, perd le
      // type et le detail, et le `catch` pose un toast court - alors que le
      // message d'un conflit est ecrit pour etre lu. L'annulation fait deja
      // le bon geste quelques lignes plus haut.
      setToastInfo({
        message: response?.message || "La modification a été refusée par le serveur.",
        type: "error",
        title: "Modification impossible",
        long: true
      });
    }
  } catch (error) {
    console.error('❌ Erreur de modification:', error);
    setToastInfo({
      message: error.message || "Erreur lors de la modification du créneau",
      type: "error"
    });
  }
};


  const handleDelete = async (event) => {
    setEventToDelete(event);
    setShowDeleteConfirm(true);
  };
  
  const handleConfirmDelete = async () => {
    try {
     
      const eventId = eventToDelete?.id;
      
      if (!eventId) {
        throw new Error("Identifiant du créneau manquant");
      }

      const response = await api.timetable.delete(eventId);
      
      if (response?.message?.includes('succès')) {
        await loadEvents();
        updateParentStats(); // Mettre à jour les stats
        setToastInfo({
          message: "Créneau supprimé avec succès",
          type: "success"
        });
        setShowDeleteConfirm(false);
        setEventToDelete(null);
      } else {
        throw new Error(response?.message || "La suppression a échoué");
      }
    } catch (error) {
      console.error('Erreur de suppression:', error);
      // Le message du serveur, pas une phrase en dur : lui seul dit pourquoi.
      setToastInfo({
        message: "Erreur lors de la suppression du créneau",
        type: "error"
      });
    } finally {
      
    }
  };


// 🆕 Fonction de gestion de la suppression en masse
const handleBulkDeleteSuccess = async (result) => {
  await loadEvents();
  updateParentStats();
  setToastInfo({
    message: `${result.deleted_count} créneau${result.deleted_count > 1 ? 'x' : ''} supprimé${result.deleted_count > 1 ? 's' : ''} avec succès`,
    type: 'success'
  });
};


const handleSubmit = async (serieData) => {
  try {
   
    
    
    // Pour un créneau unique, utiliser la route simple au lieu de createSeries
    if (serieData.type_recurrence === 'unique' && serieData.slots && serieData.slots.length === 1) {
      //  porte les memes valeurs au premier niveau que son slot
      // unique : on lit directement la premiere forme.
      const singleSlotData = {
        affectation_id: serieData.affectation_id,
        enseignant_id: serieData.enseignant_id,
        classe_id: serieData.classe_id,
        matiere_id: serieData.matiere_id,
        jour_semaine: serieData.jour_semaine,
        heure_debut: serieData.heure_debut,
        heure_fin: serieData.heure_fin,
        salle: serieData.salle || '',
        annee_scolaire_id: serieData.annee_scolaire_id,
        date: serieData.date 
      };
      
      
      const response = await api.timetable.create(singleSlotData);

      // L'issue est RENVOYÉE au modal : c'est lui qui décide de se fermer ou
      // de rester ouvert avec la saisie. Avant, il se fermait dans tous les
      // cas — y compris quand rien n'avait été créé.
      if (!response?.success) {
        const message = response?.message || response?.error
          || "Le créneau n'a pas pu être créé";
        setToastInfo({ message, type: 'error' });
        return { success: false, message };
      }

      await loadEvents();
      updateParentStats();
      setError(null);
      setToastInfo({ message: 'Créneau créé', type: 'success' });
      return { success: true };
    } else {
      // Pour les créneaux récurrents (trimestre/annuel)
      // S'assurer que affectation_id est bien présent dans chaque slot
      const seriesDataWithAffectation = {
        ...serieData,
        slots: serieData.slots.map(slot => ({
          ...slot,
          affectation_id: slot.affectation_id || serieData.affectation_id
        }))
      };
      
      
      const response = await api.timetable.createSeries(seriesDataWithAffectation);

      if (!response?.success) {
        const message = response?.message || response?.error
          || "Les créneaux n'ont pas pu être créés";
        setToastInfo({ message, type: 'error' });
        return { success: false, message };
      }

      await loadEvents();
      updateParentStats();
      setError(null);

      const combien = Array.isArray(serieData.slots) ? serieData.slots.length : 0;
      setToastInfo({
        message: combien > 1 ? `${combien} créneaux créés` : 'Créneau créé',
        type: 'success'
      });
      return { success: true };
    }
  } catch (error) {
    console.error('Erreur handleSubmit:', error);
    setError(error.message);
    const message = error.message || 'Une erreur est survenue';
    setToastInfo({ message, type: 'error' });
    return { success: false, message };
  }
};




const handleExport = async (type, selectedIds, exportType) => {
  try {
    if (!selectedIds || selectedIds.length === 0) {
      setToastInfo({
        message: 'Veuillez sélectionner au moins un élément à exporter',
        type: 'error'
      });
      return;
    }

    // Toast de chargement
    setToastInfo({ 
      message: 'Génération en cours...', 
      type: 'loading' 
    });

    if (type === 'pdf') {
      // ========== EXPORT PDF ==========
      if (selectedIds.length === 1) {
        // Export simple (un seul PDF)
        const blob = await api.timetable.exportPDF({
          type: exportType === 'class' ? 'classe' : 'enseignant',
          id: selectedIds[0],
          annee_scolaire_id: activeYear?.id
        });

        // Télécharger le PDF
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const entityName = exportType === 'class' 
          ? classes.find(c => c.id === parseInt(selectedIds[0]))?.nom 
          : teachers.find(t => t.id === parseInt(selectedIds[0]))?.nom;
        
        link.download = `emploi-du-temps-${entityName || 'export'}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        setToastInfo({
          message: 'Export PDF réussi !',
          type: 'success'
        });
        
      } else {
        // Export multiple (ZIP)
        const blob = await api.timetable.exportMultiplePDF({
          type: exportType === 'class' ? 'classe' : 'enseignant',
          ids: selectedIds.map(id => parseInt(id)),
          annee_scolaire_id: activeYear?.id
        });

        // Télécharger le ZIP
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'emplois-du-temps.zip';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        setToastInfo({
          message: `Export ZIP réussi ! ${selectedIds.length} emplois du temps générés`,
          type: 'success'
        });
      }
      
    }
    
  } catch (error) {
    console.error('❌ Erreur export:', error);
    setToastInfo({
      message: error.message || 'Erreur lors de l\'export',
      type: 'error'
    });
  } finally {
    setShowExportModal(false);
  }
};



  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    return (endHour - startHour) + (endMinute - startMinute) / 60;
  };


  const loadEvents = async () => {
  try {
    const params = { year_id: activeYear?.id };
    
    const response = await api.timetable.getAll(params);
    
    if (response?.data && Array.isArray(response.data)) {
      // 🆕 Récupérer TOUS les groupes mutualisés en une seule fois
      const groupesMutualises = new Map();
      const groupesIds = [...new Set(
        response.data
          .filter(e => e.est_cours_mutualise && e.groupe_mutualise_id)
          .map(e => e.groupe_mutualise_id)
      )];

      // Charger tous les groupes en parallèle
      await Promise.all(
        groupesIds.map(async (groupeId) => {
          try {
            const groupeResponse = await api.timetable.getGroupeMutualise(groupeId);
            if (groupeResponse.success && groupeResponse.data.classes) {
              groupesMutualises.set(String(groupeId), groupeResponse.data.classes);
            }
          } catch (error) {
            console.error(`Erreur chargement groupe ${groupeId}:`, error);
          }
        })
      );


      const normalizeScheduleTime = (value) => String(value || '').slice(0, 5);

      // Formatter les événements
      const formattedEvents = response.data.map((event) => {
        const eventDate = new Date(event.date);
        const [hours, minutes] = event.heure_debut.split(':').map(Number);
        eventDate.setHours(hours);
        eventDate.setMinutes(minutes);
        
      // 🆕 Récupérer les classes mutualisées depuis le Map
          let classesMutualisees = [];
          if (event.est_cours_mutualise) {
            const classeActuelleId = parseInt(event.classe_id);
            const classesGroupe = event.groupe_mutualise_id
              ? groupesMutualises.get(String(event.groupe_mutualise_id))
              : null;

            if (classesGroupe && classesGroupe.length > 0) {
              classesMutualisees = classesGroupe
                .filter(c => parseInt(c.classe_id) !== classeActuelleId)
                .map(c => c.classe_nom)
                .filter(Boolean);
            }

            if (classesMutualisees.length === 0) {
              classesMutualisees = [...new Set(response.data
                .filter(other =>
                  other.id !== event.id &&
                  parseInt(other.classe_id) !== classeActuelleId &&
                  String(other.enseignant_id || '') === String(event.enseignant_id || '') &&
                  String(other.matiere_id || '') === String(event.matiere_id || '') &&
                  String(other.jour_semaine || '') === String(event.jour_semaine || '') &&
                  normalizeScheduleTime(other.heure_debut) === normalizeScheduleTime(event.heure_debut) &&
                  normalizeScheduleTime(other.heure_fin) === normalizeScheduleTime(event.heure_fin) &&
                  String(other.annee_scolaire_id || '') === String(event.annee_scolaire_id || '')
                )
                .map(other => other.classe_nom || classes.find(c => parseInt(c.id) === parseInt(other.classe_id))?.nom)
                .filter(Boolean)
              )];
            }
          }
        return {
          id: event.id,
          title: subjects.find(s => s.id === event.matiere_id)?.nom || 'Cours',
          teacher: (() => {
            const teacher = teachers.find(t => t.id === event.enseignant_id);
            return teacher ? `${teacher.nom} ${teacher.prenom}` : 'Enseignant';
          })(),
          teacherId: event.enseignant_id,
          classId: event.classe_id,
          className: classes.find(c => c.id === event.classe_id)?.nom || 'Classe inconnue',
          date: eventDate,
          duration: calculateDuration(event.heure_debut, event.heure_fin),
          subject_id: event.matiere_id,
          salle: event.salle,
          est_annule: event.est_annule,
          affectation_id: event.affectation_id,
          // 🆕 Nouveaux champs
          est_cours_mutualise: event.est_cours_mutualise || false,
          groupe_mutualise_id: event.groupe_mutualise_id,
          classes_mutualisees: classesMutualisees
        };
      });
      
      setEvents(formattedEvents);
    }
  } catch (error) {
    console.error('❌ Erreur chargement:', error);
    setError(`Erreur lors du chargement des créneaux: ${error.message}`);
  }
};


  // Charger les données initiales

useEffect(() => {
  const loadInitialData = async () => {
    try {
      // `api.employes.getAll()` rendait les 69 employes actifs, verses tels
      // quels dans `teachers` : la recherche d'enseignant, l'export « Par
      // enseignants », le volume horaire et l'edition d'un creneau
      // proposaient donc le comptable et le chauffeur.
      //
      // Verifie avant de restreindre : les 30 enseignants presents dans
      // `emplois_du_temps` appartiennent tous au groupe « Enseignant », donc
      // aucun creneau ne perd le nom de son titulaire.
      const [classesData, enseignantsData, subjectsData, sectionsData] = await Promise.all([
        api.classes.getAll(),
        api.employes.getEnseignants(),
        api.matieres.getAll(),
        api.sections.getAll()
      ]);

      setClasses(Array.isArray(classesData) ? classesData : []);
      setSubjects(Array.isArray(subjectsData) ? subjectsData : []);
      
      if (enseignantsData?.success && Array.isArray(enseignantsData.data)) {
        setTeachers(enseignantsData.data);
      }
      
      if (sectionsData?.success && Array.isArray(sectionsData.data)) {
        setSections(sectionsData.data);
      }
    } catch (error) {
      setError("Erreur lors du chargement des données");
    } finally {
      setPremierChargement(false);
    }
  };

  if (activeYear) {
    loadInitialData();
  } else {
    // Sans annee active, il n'y a rien a attendre : la page dirait sinon
    // qu'elle charge pour toujours.
    setPremierChargement(false);
  }
}, [activeYear]);

// Chargement des events quand les données sont prêtes
useEffect(() => {
  if (activeYear && subjects.length > 0 && teachers.length > 0 && classes.length > 0) {
    loadEvents();
  }
}, [activeYear?.id, subjects.length, teachers.length, classes.length]);

  
 
  // Fonctions mémorisées pour optimiser les performances
  const handleClose = useCallback(() => {
    setShowRecurringModal(false);
  }, []);
  
  const memoizedSubmit = useCallback(async (serieData) => {
    try {
      
      const response = await api.timetable.createSeries(serieData);
      
      if (response.success) {
        await loadEvents();
        updateParentStats();
        setShowRecurringModal(false);
        setError(null);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      
    }
  }, [loadEvents, updateParentStats]);
  
  const memoizedEvents = useMemo(() => events, [events]);

  // = useCallback((type, value) => {
  //setFilters(prev => ({ ...prev, [type]: value }));
//}, []);


  if (premierChargement) {
    return <LoadingSpinner message="Chargement de l'emploi du temps..." size="lg" />;
  }

  return (
    <div className="w-full p-0">
      {/* Bouton création - protégé par permission CREATE */}



  {/* ========== HEADER MODERNE - DÉBUT ========== */}
<div className="mb-6">
  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg border border-gray-200 p-4 lg:p-5">
    <div className="grid grid-cols-1 xl:grid-cols-[210px_minmax(0,1fr)] gap-4 xl:gap-5 items-start">
      {/* ========== ACTIONS GROUPEES ========== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-1 gap-2.5">
        <ProtectedComponent module="ELEVES" sousModule="EMPLOIS_DU_TEMPS" action="CREATE">
          <button
            onClick={() => setShowRecurringModal(true)}
            className="group relative w-full min-h-[42px] px-3.5 py-2.5 rounded-lg text-white font-medium text-[13px] shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #246990 0%, #1e5576 100%)'
            }}
          >
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            <div className="relative grid w-full max-w-[170px] grid-cols-[18px_minmax(0,1fr)] items-center gap-2 mx-auto text-left whitespace-nowrap">
              <CalendarIcon className="w-3.5 h-3.5 shrink-0 justify-self-center" />
              <span>Créer des créneaux</span>
            </div>
          </button>
        </ProtectedComponent>

        {/* Le bouton le plus destructeur du module n'avait aucune garde, alors
            que « Creer des creneaux » juste au-dessus en a une. DELETE est
            accorde aux memes roles que CREATE : personne ne perd l'acces. */}
        <ProtectedComponent module="ELEVES" sousModule="EMPLOIS_DU_TEMPS" action="DELETE">
          <button
            onClick={() => setShowDeleteBulkModal(true)}
            className="group relative w-full min-h-[42px] px-3.5 py-2.5 rounded-lg text-white font-medium text-[13px] shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #c66d59 0%, #ae7467 100%)'
            }}
          >
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            <div className="relative grid w-full max-w-[170px] grid-cols-[18px_minmax(0,1fr)] items-center gap-2 mx-auto text-left whitespace-nowrap">
              <Trash2 className="w-3.5 h-3.5 shrink-0 justify-self-center" />
              <span>Supprimer en masse</span>
            </div>
          </button>
        </ProtectedComponent>

        {/* Une vue : la cle de consultation suffit, et c'est celle qui manquait. */}
        <ProtectedComponent module="ELEVES" sousModule="EMPLOIS_DU_TEMPS" action="VIEW">
          <button
            onClick={onVolumeHoraireClick}
            className={`group relative w-full min-h-[42px] px-3.5 py-2.5 rounded-lg font-medium text-[13px] shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 overflow-hidden ${
              showVolumeHoraire
                ? 'bg-gray-500 text-white'
                : 'bg-white text-gray-700 border border-gray-200'
            }`}
          >
            {showVolumeHoraire ? (
              <div className="relative grid w-full max-w-[170px] grid-cols-[18px_minmax(0,1fr)] items-center gap-2 mx-auto text-left whitespace-nowrap">
                <X className="w-3.5 h-3.5 shrink-0 justify-self-center" />
                <span>Fermer</span>
              </div>
            ) : (
              <>
                <div className="absolute inset-0 bg-gradient-to-r from-[#319190] to-[#63b5b4] opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <div className="relative grid w-full max-w-[170px] grid-cols-[18px_minmax(0,1fr)] items-center gap-2 mx-auto text-left whitespace-nowrap">
                  <Clock className="w-3.5 h-3.5 text-[#319190] shrink-0 justify-self-center" />
                  <span>Volumes Horaires</span>
                </div>
              </>
            )}
          </button>
        </ProtectedComponent>

        {/* La fenetre qu'il ouvre exige deja EXPORT_PDF : sans la cle, le bouton
            s'affichait, on cliquait, et il ne se passait rien. */}
        <ProtectedComponent module="ELEVES" sousModule="EMPLOIS_DU_TEMPS" action="EXPORT_PDF">
          <button
            onClick={onExportClick}
            className="group relative w-full min-h-[42px] px-3.5 py-2.5 rounded-lg text-white font-medium text-[13px] shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #c66d59 0%, #ae7467 100%)'
            }}
          >
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            <div className="relative grid w-full max-w-[170px] grid-cols-[18px_minmax(0,1fr)] items-center gap-2 mx-auto text-left whitespace-nowrap">
              <Download className="w-3.5 h-3.5 shrink-0 justify-self-center" />
              <span>Exporter</span>
            </div>
          </button>
        </ProtectedComponent>
      </div>

      {/* ========== FILTRES (CLASSES / ENSEIGNANTS) ========== */}
      <ProtectedComponent module="ELEVES" sousModule="EMPLOIS_DU_TEMPS" action="FILTER">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3.5 lg:p-4 min-w-0">
          <div className={`grid grid-cols-1 gap-4 ${!selectedTeacher ? 'lg:grid-cols-[minmax(380px,1.2fr)_minmax(300px,0.8fr)]' : ''}`}>
            {!selectedTeacher && (
              <div className="space-y-2.5 min-w-0">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  <GraduationCap className="w-3.5 h-3.5 text-[#319190] shrink-0" />
                  <span>Sélectionner une classe</span>
                </label>
                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[205px] overflow-y-auto">
                  {sortedSections.map(section => {
                    const sectionClasses = (classesBySection[section.id] || []).filter(c =>
                      c.nom.toLowerCase().includes(searchQuery.toLowerCase())
                    );

                    if (sectionClasses.length === 0) return null;

                    const isExpanded = expandedSections.has(section.id);

                    return (
                      <div key={section.id} className="border-b last:border-b-0">
                        <div
                          className="flex items-center justify-between gap-3 p-2 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => toggleExpansion(section.id)}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="text-gray-400 shrink-0">
                              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            </div>
                            <div className="text-[#319190] shrink-0">
                              {getSectionIcon(section.nom)}
                            </div>
                            <span className="font-medium text-[13px] text-gray-900 truncate">{section.nom}</span>
                          </div>
                          <span className="text-xs text-gray-500 shrink-0">{sectionClasses.length}</span>
                        </div>

                        {isExpanded && (
                          <div className="bg-gray-50 p-2 space-y-1">
                            {sectionClasses
                              .sort((a, b) => (a.ordre && b.ordre) ? a.ordre - b.ordre : a.nom.localeCompare(b.nom))
                              .map(classe => {
                                const isSelected = selectedClass === classe.id.toString();
                                return (
                                  <label
                                    key={classe.id}
                                    className={`flex items-center gap-2 p-1.5 rounded cursor-pointer transition-all text-[13px] ${
                                      isSelected
                                        ? 'bg-[#319190] text-white'
                                        : 'hover:bg-white'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="selectedClass"
                                      checked={isSelected}
                                      onChange={() => handleClassChange(classe.id.toString())}
                                      className="sr-only"
                                    />
                                    <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                      isSelected ? 'border-white' : 'border-gray-300'
                                    }`}>
                                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white"></div>}
                                    </div>
                                    <span className="font-medium truncate">{classe.nom}</span>
                                  </label>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2.5 min-w-0">
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                <Search className="w-3.5 h-3.5 text-[#319190] shrink-0" />
                <span>Rechercher un enseignant</span>
              </label>
              <TeacherSearchSelect
                teachers={teachers}
                selectedTeacher={selectedTeacher}
                onSelect={handleTeacherSelect}
                onClear={handleTeacherClear}
              />

              {(selectedClass || selectedTeacher) && (
                <button
                  onClick={handleReset}
                  className="w-full text-[13px] px-3 py-1.5 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
        </div>
      </ProtectedComponent>
    </div>
  </div>
</div>
{/* ========== HEADER MODERNE - FIN ========== */}

  


      {/* Légende des couleurs */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-6 text-sm">
          <span className="font-medium text-gray-700">Légende :</span>
          
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-400 border border-green-500"></div>
            <span className="text-gray-600">Créneau normal</span>
          </div>
          

         {/* <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-400 border border-blue-500"></div>
            <span className="text-gray-600">Créneau mutualisé</span>
          </div> */}
          
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-400 border border-red-500"></div>
            <span className="text-gray-600">Créneau annulé</span>
          </div>
        </div>
      </div>


      {/* Consultation Volume Horaire - S'AFFICHE APRÈS LES BOUTONS */}
      {showVolumeHoraire && (
        <div className="mb-6" id="volume-horaire-section">
          <ConsultationVolumeHoraire
            teachers={teachers}
            activeYear={activeYear}
            onClose={() => setShowVolumeHoraire(false)}
          />
        </div>
      )}

   

      {/* Calendrier - protégé par permission VIEW */}
      <ProtectedComponent 
        module="ELEVES" 
        sousModule="EMPLOIS_DU_TEMPS" 
        action="VIEW"
        fallback={<div className="p-8 text-center">Vous n'avez pas l'autorisation de consulter les emplois du temps.</div>}
      >
        <Calendar 
          events={filteredEvents}
          filters={filters}
          onEdit={(event) => {
            if (hasGranularPermission("ELEVES", "EMPLOIS_DU_TEMPS", "EDIT")) {
              handleEdit(event);
            }
          }}
          onDelete={(event) => {
            if (hasGranularPermission("ELEVES", "EMPLOIS_DU_TEMPS", "DELETE")) {
              handleDelete(event);
            }
          }}
          onCancel={(event) => {
          if (hasGranularPermission("ELEVES", "EMPLOIS_DU_TEMPS", "EDIT")) {
            handleCancelClick(event);
          }
        }}
      />
     </ProtectedComponent>

      {/* Modal de confirmation de suppression */}
      <ProtectedComponent module="ELEVES" sousModule="EMPLOIS_DU_TEMPS" action="DELETE">
        <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Confirmer la suppression"
        description="Êtes-vous sûr de vouloir supprimer ce créneau ?"
        type="error"
        confirmText="Supprimer"
        cancelText="Annuler"
        />
      </ProtectedComponent>


  
        

      {/* Toast notifications */}
      {/* PorteurNotification : sort de la pile des Dialog, et bascule en
          format long pour les messages du serveur, que le bandeau etroit
          tronquait. */}
      <PorteurNotification notification={toastInfo} onFermer={() => setToastInfo(null)} />

      {/* Affichage des erreurs */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-lg z-50">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-700 hover:text-red-900">
            ×
          </button>
        </div>
      )}

    

      {/* Modal de création de créneaux récurrents */}
      <ProtectedComponent module="ELEVES" sousModule="EMPLOIS_DU_TEMPS" action="CREATE">
        <RecurringModal
          isOpen={showRecurringModal}
          onClose={() => setShowRecurringModal(false)}
          onSubmit={handleSubmit}
          classes={classes}
          teachers={teachers}
          subjects={subjects}
          activeYear={activeYear}
          events={events}
          onOpenEmploiDuTemps={() => {
          // On ferme la fenêtre qui l'ouvre : deux calques l'un sur l'autre,
          // et on retombait sur l'étape 0 des créneaux récurrents en sortant.
          setShowRecurringModal(false);
          setShowEmploiDuTempsModal(true);
        }}
        />
      </ProtectedComponent>   


      {/* Modal d'export - protégé par permissions EXPORT */}
     <ProtectedComponent 
      module="ELEVES" 
      sousModule="EMPLOIS_DU_TEMPS" 
      action="EXPORT_PDF"
    >
      <ExportTimetableModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        classes={classes}
        teachers={teachers}
        activeYear={activeYear}
        onExport={handleExport}
      />
    </ProtectedComponent>


      {/* Modal d'édition de créneau */}
      <ProtectedComponent module="ELEVES" sousModule="EMPLOIS_DU_TEMPS" action="EDIT">
        <EditTimeSlotModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEvent(null);
          }}
          onSubmit={handleEditSubmit}
          event={selectedEvent}
          classes={classes}
          teachers={teachers}
          subjects={subjects}
          anneeScolaire={activeYear}
        />
      </ProtectedComponent>

      
      {/* Modal annulation/réactivation */}
      <ProtectedComponent module="ELEVES" sousModule="EMPLOIS_DU_TEMPS" action="EDIT">
        <CancelTimeSlotModal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setSelectedEvent(null);
          }}
          onSubmit={handleCancelSubmit}
          event={selectedEvent}
          mode={cancelMode}
          anneeScolaire={activeYear}
        />
      </ProtectedComponent>




      <EmploiDuTempsAnnuelModal
        isOpen={showEmploiDuTempsModal}
        onClose={() => setShowEmploiDuTempsModal(false)}
        activeYear={activeYear}
        onSuccess={handleEmploiDuTempsCreated}
      />

      {/* Montage conditionnel : `isOpen` ne court-circuitait que le rendu,
          et la fenetre n'etait JAMAIS demontee. On cochait quarante classes,
          on choisissait « Toute l'annee », on fermait - et tout etait encore
          arme a la reouverture. */}
      {showDeleteBulkModal && (
        <DeleteBulkTimetableModal
          isOpen={showDeleteBulkModal}
          onClose={() => setShowDeleteBulkModal(false)}
          classes={classes}
          sections={sections}
          activeYear={activeYear}
          onDeleteSuccess={handleBulkDeleteSuccess}
        />
      )}
    </div>
  );
};

export default TimetableManager;